import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent } from "react";
import { invoke } from "./tauri-web";
import { listen } from "./tauri-web";
import { platform } from "./tauri-web";
import { openFileDialog, saveFileDialog } from "./tauri-web";
import { writeTextFile } from "./tauri-web";
import { supabase } from "./supabase";
import { AiConsent, AI_CONSENT_KEY, signInIos, signInMac, openAccountDelete } from "./ios-native-kit";
import { AccountMenu } from "./AccountMenu";
import { TagMenu, AskCrate } from "./TagMenu";
import {
  store, summarize, summaryCopy, lastUsedCopy, titleCase, seasonEmoji, normalizeToken,
  DEFAULT_SEASONS, DEFAULT_CATEGORIES, DEFAULT_LOCATIONS, todayLocal,
  type Item, type Facets, type StashSignals,
} from "./store";
import { recapSeason, askStash, fileBoxFromPhoto, openWebLogin, crossesFence, FENCE_REDIRECT, BrainError, type BrainErrorCode, type FiledBoxItem } from "./brain";
import { readDownscaledPhoto } from "./photo";
import { OrioraBadge } from "./OrioraBadge";
import { PhotoViewer } from "./PhotoViewer";
import { BackupMenu } from "./BackupMenu";
import { buildExport } from "./export";
import Chat from "./chat/Chat";
import emptyCatalogUrl from "./assets/empty-catalog.svg";
import emptyPlaceUrl from "./assets/empty-place.svg";
import {
  Sparkles, Trash2, Lock, Package, WifiOff, RefreshCw, MapPin, CalendarClock, Search, CheckCircle2,
  Image as ImageIcon, Camera,
} from "lucide-react";

const LIST_CAP = 50000;

// ── Line-icon MARK set (ported from Claude Design "Mark" component, project ee2551b0).
// Inline SVG, no bundled assets, never emoji (R6). Places, seasons and small UI glyphs
// all render from here. Color is driven through CSS `color` + `currentColor` so it is
// WebKit-safe (SVG presentation attributes do NOT resolve var() on iOS Safari).
type MarkKind =
  | "home" | "attic" | "garage" | "closet" | "basement" | "office" | "underbed" | "elsewhere"
  | "box" | "spring" | "summer" | "fall" | "winter"
  | "ask" | "recap" | "settings" | "back" | "close" | "chevronRight" | "plus";

function MarkGlyph({ kind }: { kind: MarkKind }) {
  switch (kind) {
    case "home": return <><polyline points="4,12 12,5 20,12" /><polyline points="6,11 6,19 18,19 18,11" /></>;
    case "attic": return <><polyline points="4,13 12,5 20,13" /><circle cx="12" cy="11.5" r="1.2" fill="currentColor" stroke="none" /></>;
    case "garage": return <><polyline points="4,9 12,4 20,9" /><rect x="4" y="9" width="16" height="11" rx="1.5" /><line x1="6" y1="13" x2="18" y2="13" /><line x1="6" y1="17" x2="18" y2="17" /></>;
    case "closet": return <><circle cx="12" cy="5" r="1.3" /><polygon points="12,6.3 5,15 19,15" /></>;
    case "basement": return <><polyline points="5,6 10,6 10,11 14,11 14,16 19,16" /><line x1="19" y1="16" x2="19" y2="19" /><line x1="16" y1="19" x2="19" y2="19" /></>;
    case "office": return <><path d="M9,9 V7 a3,3 0 0 1 6,0 V9" /><rect x="4" y="9" width="16" height="10" rx="1.5" /><line x1="4" y1="13" x2="20" y2="13" /></>;
    case "underbed": return <><rect x="3" y="13" width="18" height="5" rx="1" /><line x1="3" y1="8" x2="3" y2="18" /><line x1="5" y1="18" x2="5" y2="20" /><line x1="19" y1="18" x2="19" y2="20" /></>;
    case "elsewhere": return <><line x1="12" y1="4" x2="12" y2="20" /><line x1="5" y1="7" x2="19" y2="17" /><line x1="19" y1="7" x2="5" y2="17" /></>;
    case "box": return <><polygon points="4,8 12,4 20,8 20,17 12,21 4,17" /><polyline points="4,8 12,12 20,8" /><line x1="12" y1="12" x2="12" y2="21" /></>;
    case "spring": return <><line x1="12" y1="20" x2="12" y2="11" /><path d="M12,13 C8,13 7,9 7,7 C10,7 12,9 12,13" /><path d="M12,11 C16,11 17,7 17,5 C14,5 12,7.5 12,11" /></>;
    case "summer": return <><circle cx="12" cy="12" r="4" /><line x1="12" y1="2.5" x2="12" y2="5" /><line x1="12" y1="19" x2="12" y2="21.5" /><line x1="2.5" y1="12" x2="5" y2="12" /><line x1="19" y1="12" x2="21.5" y2="12" /><line x1="5.6" y1="5.6" x2="7.4" y2="7.4" /><line x1="16.6" y1="16.6" x2="18.4" y2="18.4" /><line x1="5.6" y1="18.4" x2="7.4" y2="16.6" /><line x1="16.6" y1="7.4" x2="18.4" y2="5.6" /></>;
    case "fall": return <><path d="M12,20 C6,18 5,11 9,6 C13,3 18,6 18,11 C18,16 15,19 12,20 Z" /><line x1="12" y1="20" x2="12" y2="22" /><path d="M12,18 C11,14 11,10 12,7" /></>;
    case "winter": return <><line x1="12" y1="3" x2="12" y2="21" /><line x1="4.4" y1="7.5" x2="19.6" y2="16.5" /><line x1="19.6" y1="7.5" x2="4.4" y2="16.5" /><circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" /></>;
    case "ask": return <><rect x="3" y="5" width="18" height="12" rx="6" /><polyline points="8,17 6.5,21 10,17" /><line x1="7" y1="10" x2="17" y2="10" /><line x1="7" y1="14" x2="13" y2="14" /></>;
    case "recap": return <><path d="M4,12 a8,8 0 0 1 13.6,-5.6" /><path d="M20,12 a8,8 0 0 1 -13.6,5.6" /><polyline points="17.6,6.4 20,6 19.6,8.4" /><polyline points="6.4,17.6 4,18 4.4,15.6" /></>;
    case "settings": return <><line x1="5" y1="7" x2="19" y2="7" /><line x1="5" y1="12" x2="19" y2="12" /><line x1="5" y1="17" x2="19" y2="17" /><circle cx="9" cy="7" r="2" fill="currentColor" stroke="none" /><circle cx="15" cy="12" r="2" fill="currentColor" stroke="none" /><circle cx="11" cy="17" r="2" fill="currentColor" stroke="none" /></>;
    case "back": return <polyline points="15,4 7,12 15,20" />;
    case "close": return <><line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" /></>;
    case "chevronRight": return <polyline points="9,5 15,12 9,19" />;
    case "plus": return <><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></>;
    default: return null;
  }
}

function Mark({ kind, size = 24, color = "currentColor", strokeWidth = 1.6, title }: { kind: MarkKind; size?: number; color?: string; strokeWidth?: number; title?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
      aria-hidden={title ? undefined : true} role={title ? "img" : undefined}
      style={{ color, display: "block", flexShrink: 0 }}>
      {title ? <title>{title}</title> : null}
      <MarkGlyph kind={kind} />
    </svg>
  );
}

// Map an app place-name / season-token onto the mark set.
function placeKind(place: string): MarkKind {
  const p = (place || "").toLowerCase();
  if (/(attic|loft)/.test(p)) return "attic";
  if (/(garage|shed)/.test(p)) return "garage";
  if (/(basement|cellar)/.test(p)) return "basement";
  if (/(office|study|desk)/.test(p)) return "office";
  if (/(under.?bed|under the bed)/.test(p)) return "underbed";
  if (/(closet|wardrobe|cupboard|dresser|hallway|hall|linen|bedroom)/.test(p)) return "closet";
  if (/(box|crate|bag|bin|boot|car|trunk)/.test(p)) return "box";
  if (/elsewhere/.test(p)) return "elsewhere";
  return "box";
}
function seasonKind(season: string): MarkKind {
  const s = (season || "").toLowerCase();
  if (/(spring|easter)/.test(s)) return "spring";
  if (/(summer|beach|camping)/.test(s)) return "summer";
  if (/(winter|christmas|ski|snow)/.test(s)) return "winter";
  return "fall"; // autumn, halloween, thanksgiving, rainy + unknowns
}

function PlaceMark({ place, size = 26, color = "var(--accent)" }: { place: string; size?: number; color?: string }) {
  return <Mark kind={placeKind(place)} size={size} color={color} />;
}
function SeasonMark({ season, size = 20, color = "var(--accent)" }: { season: string; size?: number; color?: string }) {
  return <Mark kind={seasonKind(season)} size={size} color={color} title={titleCase(season)} />;
}

// R1 — the map of places: group by the location's first segment ("Attic — red box" → "Attic").
function placeOf(location: string): string {
  const seg = (location || "").split(/—|,|–/)[0].trim();
  return seg ? titleCase(seg) : "Elsewhere";
}

// R4 — numbers survive as words in the one-line summary.
const NUM_WORDS = ["no", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve"];
const spellNum = (n: number) => (n >= 0 && n < NUM_WORDS.length ? NUM_WORDS[n] : String(n));

// SCREENSHOT=0 — sim-only auth/consent bypass for identity screenshots. MUST stay 0 in committed/shipped code.
const SCREENSHOT = 0;

export default function App() {
  const [mounted, setMounted] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  // AI-consent gate (APP-READY-STANDARD §2/§3, Apple Guideline 5.1.1(i)). Shown ONCE on first
  // launch before anything else — names the data sent + the AI vendors. Persisted in localStorage.
  const [consented, setConsented] = useState<boolean>(() => {
    try { return localStorage.getItem(AI_CONSENT_KEY) === "1"; } catch { return false; }
  });

  // A deleted/revoked account keeps a locally-valid (unexpired) JWT, so getSession() still
  // returns it and the app looks signed-in. getUser() asks the SERVER — on a real auth
  // rejection (401/403) we purge the dead session and drop to login. Network errors (no
  // status) are ignored so an offline-but-valid user is never signed out.
  const revalidate = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;
      const { error } = await supabase.auth.getUser();
      const status = (error as { status?: number } | null)?.status;
      if (error && (status === 401 || status === 403)) {
        try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
        setEmail(null); setAuthed(false);
      }
    } catch { /* network/unknown → keep session (offline-tolerant) */ }
  }, []);

  useEffect(() => {
    // onAuthStateChange fires INITIAL_SESSION after async filesystem storage loads —
    // avoids the cold-launch race where getSession() returned null before storage was ready.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      setAuthed(session !== null);
      setEmail(session?.user?.email ?? null);
      setMounted(true);
      if (_e === 'INITIAL_SESSION' && session) void revalidate();
    });
    const timeout = setTimeout(() => setMounted(true), 3000);
    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, [revalidate]);

  // Re-validate whenever the app returns to the foreground (catches "deleted my account,
  // came back, still signed in").
  useEffect(() => {
    const recheck = () => { if (document.visibilityState === "visible") void revalidate(); };
    document.addEventListener("visibilitychange", recheck);
    window.addEventListener("focus", recheck);
    return () => { document.removeEventListener("visibilitychange", recheck); window.removeEventListener("focus", recheck); };
  }, [revalidate]);

  // Deep-link auth: web login returns oriora-stash://auth#access_token=…&refresh_token=…
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("auth:deep-link", async (e) => {
      try {
        const u = new URL(e.payload);
        const frag = new URLSearchParams(u.hash.replace(/^#/, ""));
        const access_token = frag.get("access_token");
        const refresh_token = frag.get("refresh_token");
        if (access_token && refresh_token) await supabase.auth.setSession({ access_token, refresh_token });
      } catch (err) {
        console.error("deep-link auth failed", err);
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // Account-deletion handback: the in-app delete window finished + navigated to
  // oriora-stash://deleted. Sign the app out so it drops to the logged-out screen.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen("account:deleted", async () => {
      try { await supabase.auth.signOut({ scope: "local" }); } catch { /* ignore */ }
      setEmail(null); setAuthed(false);
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  // First-launch AI-consent gate — before login + before any AI feature is reachable.
  if (!consented && !SCREENSHOT) {
    return <AiConsent brand="Stash"
      onAccept={() => { try { localStorage.setItem(AI_CONSENT_KEY, "1"); } catch { /* still consented this session */ } setConsented(true); }} />;
  }
  if (!mounted && !SCREENSHOT) return <div style={{ height: "100%", display: "grid", placeItems: "center" }} className="muted">Loading…</div>;
  if (!authed && !SCREENSHOT) return <Login />;
  return <Home email={SCREENSHOT ? "you@oriora.app" : email} onSignedOut={() => { setEmail(null); setAuthed(false); }} />;
}

function Login() {
  const [busy, setBusy] = useState(false);
  return (
    <main style={{ height: "100dvh", display: "flex", flexDirection: "column", overflowY: "auto", WebkitOverflowScrolling: "touch" as any, boxSizing: "border-box", padding: "max(1.5rem, env(safe-area-inset-top)) 1.5rem max(1.5rem, env(safe-area-inset-bottom))" }}>
      <div className="card" style={{ width: "100%", maxWidth: 380, padding: "2rem", textAlign: "center", margin: "auto" }}>
        <div style={{ marginBottom: "0.4rem", display: "flex", justifyContent: "center" }}><PlaceMark place="box" size={44} /></div>
        <div style={{ color: "var(--accent)", fontWeight: 700, fontSize: "var(--fs-sm)" }}>Stash</div>
        <h1 className="serif" style={{ fontSize: "1.5rem", margin: "0.4rem 0 0.4rem" }}>Sign in with Oriora</h1>
        <p className="muted" style={{ fontSize: "var(--fs-xs)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
          Uses your Oriora account — opens to sign in, then returns here. Your catalog lives on this
          device; the AI recap runs through your Oriora account. Oriora doesn't retain what you send for an AI action. You can also back up to your own GitHub or Google Drive.
        </p>
        <button className="btn btn-primary" style={{ width: "100%" }} disabled={busy}
          onClick={async () => {
            setBusy(true);
            // iOS → in-app ASWebAuthenticationSession (Apple Guideline 4, #61); desktop → in-app login window.
            try {
              const p = platform();
              if (p === "ios") await signInIos("oriora-stash");
              else if (p === "macos") await signInMac("oriora-stash");
              // Web: same-origin login. Go to the site login and return to this app's path; the
              // Supabase session is shared on the orioralabs.com origin, so we come back signed in.
              else if (p === "web") window.location.href = "/login?next=/oriora-stash/";
              else await openWebLogin();
            }
            catch { /* user dismissed the sheet or it failed — reset and let them retry */ }
            finally { setBusy(false); }
          }}>
          {busy ? "Signing in…" : "Sign in with Oriora"}
        </button>
      </div>
    </main>
  );
}

type Filter = { kind: "season" | "location" | "category"; value: string } | null;

// ── READY-STATE 2026-09-19 — the FIRST screen, zero items ───────────────────────────────────
// A stash is not a list; it is three questions — what comes out next (season/occasion), what has
// never come out at all (`last_used_on` empty), and where a thing lives. Those are PARITY-MATRIX
// rows 4, 6 and 2, and none of them existed on the screen until the catalogue did. The lanes are
// drawn empty and labelled, then one line files a real thing into them: what it is, where it
// lives, and optionally the season it belongs to.
// ⚠️ NOT the photo path — "File a box from a photo" is a billed vision call and is left exactly
// where it is, untouched and unexercised. This door is local: one existing store write.
const START_SEASON_PICKS = ["christmas", "winter", "summer", "camping"] as const;

function StartShelf({ onFiled }: { onFiled: () => void | Promise<void> }) {
  const [name, setName] = useState("");
  const [where, setWhere] = useState("");
  const [season, setSeason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function file() {
    const n = name.trim();
    if (!n) { setErr("Name the thing — where it lives and when you need it can follow."); return; }
    setBusy(true); setErr(null);
    try {
      await store.createItem({ name: n, location: where.trim(), season, quantity: 1 });
      await onFiled();
    } catch (e) {
      setErr(`Couldn't file that — ${e instanceof Error ? e.message : String(e)}`);
      setBusy(false);
    }
  }

  return (
    <div className="stash-start">
      <p className="stash-start-lede">
        Three questions this answers once things are in it — and nothing is in it yet.
      </p>

      <div className="stash-start-lanes" aria-label="What the stash tracks">
        <div className="stash-lane">
          <span className="stash-lane-k">Out next</span>
          <span className="stash-lane-v">Tag a thing with its season or occasion and it surfaces when that comes round.</span>
        </div>
        <div className="stash-lane">
          <span className="stash-lane-k">Never used</span>
          <span className="stash-lane-v">Anything you have kept but never taken out again is listed on its own.</span>
        </div>
        <div className="stash-lane">
          <span className="stash-lane-k">By place</span>
          <span className="stash-lane-v">Every place you keep things, and what is in it — ask "where are my winter boots?".</span>
        </div>
      </div>

      <div className="stash-start-rule"><span>File the first thing</span></div>

      <div className="stash-start-row">
        <input value={name} onChange={(e) => setName(e.target.value)} aria-label="What it is"
          placeholder="What — e.g. winter boots" className="stash-start-name"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void file(); } }} />
        <input value={where} onChange={(e) => setWhere(e.target.value)} aria-label="Where it lives"
          placeholder="Where" className="stash-start-where"
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void file(); } }} />
      </div>

      <div className="stash-start-chips" role="group" aria-label="Season (optional)">
        {START_SEASON_PICKS.map((s) => (
          <button key={s} type="button" className={`chip${season === s ? " selected" : ""}`}
            onClick={() => setSeason(season === s ? "" : s)}>{titleCase(s)}</button>
        ))}
      </div>

      {err && <div className="stash-start-err">{err}</div>}

      <button className="btn btn-primary btn-pill stash-start-go" disabled={busy} onClick={() => void file()}>
        {busy ? "Filing…" : "File it"}
      </button>

      <p className="stash-start-foot">
        The catalogue stays on this device, the CSV export is free, and there is nothing to
        subscribe to — a backup goes to your own GitHub or Drive, not to our servers.
      </p>
    </div>
  );
}

function Home({ email, onSignedOut }: { email: string | null; onSignedOut: () => void }) {
  const [items, setItems] = useState<Item[]>([]);
  const [facets, setFacets] = useState<Facets>({ seasons: [], locations: [], categories: [] });
  const [filter, setFilter] = useState<Filter>(null);
  const [editing, setEditing] = useState<Item | null>(null);
  const [showFileBox, setShowFileBox] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Item | null>(null);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && navigator.onLine === false);
  // Chat panel (ADDITIVE): a natural-language front door over the SAME capabilities — does not change
  // any existing screen. onChanged → reload() refreshes the catalog after a chat-driven mutation.
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => { window.removeEventListener("online", goOnline); window.removeEventListener("offline", goOffline); };
  }, []);

  const recapRef = useRef<HTMLDivElement>(null);
  const exportRef = useRef<(() => void) | null>(null);

  const reload = useCallback(async () => {
    const [list, fac] = await Promise.all([store.listItems({ limit: LIST_CAP }), store.listFacets()]);
    setItems(list);
    setFacets(fac);
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  // Native-menu actions (Actions menu in the macOS menu bar): Add / Recap / Export.
  useEffect(() => {
    let unlisten: (() => void) | null = null;
    listen<string>("menu:action", (e) => {
      switch (e.payload) {
        case "add": setEditing(null); setShowAdd(true); break;
        case "recap": recapRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }); break;
        case "export": exportRef.current?.(); break;
      }
    }).then((u) => { unlisten = u; });
    return () => { if (unlisten) unlisten(); };
  }, []);

  const summary = useMemo(() => summarize(items), [items]);

  // R1 — the map of places, grouped from the location free-text's first segment.
  const [openPlace, setOpenPlace] = useState<string | null>(null);
  const places = useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const i of items) { const p = placeOf(i.location); m.set(p, [...(m.get(p) ?? []), i]); }
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [items]);
  const placeItems = useMemo(() => (openPlace ? (places.find(([p]) => p === openPlace)?.[1] ?? []) : []), [places, openPlace]);

  const filtered = useMemo(() => {
    if (!filter) return items;
    if (filter.kind === "season") return items.filter((i) => i.season.toLowerCase() === filter.value.toLowerCase());
    if (filter.kind === "location") return items.filter((i) => i.location.toLowerCase() === filter.value.toLowerCase());
    return items.filter((i) => i.category.toLowerCase() === filter.value.toLowerCase());
  }, [items, filter]);

  const doExport = useCallback(() => {
    if (items.length === 0) return;
    const { csv, filename } = buildExport(items);
    void (async () => { const p = await saveFileDialog({ defaultPath: filename }); if (p) await writeTextFile(p, csv); })();
  }, [items]);
  exportRef.current = doExport;

  function startEdit(item?: Item) { setEditing(item ?? null); setShowAdd(true); }
  async function doDelete(id: string) {
    setConfirmDelete(null);
    await store.deleteItem(id);
    await reload();
  }
  async function doMarkUsed(id: string) {
    await store.markUsed(id, todayLocal());
    await reload();
  }

  return (
    <main style={{
      maxWidth: 720, margin: "0 auto",
      // Safe-area aware so content clears the iPhone notch/home-indicator (viewport-fit=cover).
      padding: "max(2rem, env(safe-area-inset-top)) max(1.5rem, env(safe-area-inset-right)) calc(2rem + env(safe-area-inset-bottom)) max(1.5rem, env(safe-area-inset-left))",
    }}>
      {offline && (
        <div role="status" className="row" style={{ gap: "0.45rem", fontSize: "var(--fs-xs)", color: "var(--warning)", background: "rgba(217,147,58,0.12)", border: "1px solid rgba(217,147,58,0.35)", borderRadius: 8, padding: "0.45rem 0.75rem", marginBottom: "1rem" }}>
          <WifiOff size={14} style={{ flexShrink: 0 }} /> AI recap paused — your catalog still saves locally
        </div>
      )}

      {/* 🔴 IDENTITY (2026-08-30): there is NO wordmark header row. The NEXT-OUT season is the
          header — a storage app opens on what is coming out next, not on the app's own name.
          This app carried the loudest version of the banned signature: an italic wordmark beside
          FOUR round buttons. The two primary actions became square crate controls on the rail,
          and backup + account collapsed into ONE clipped-corner label control (TagMenu).
          Do not reintroduce a wordmark strip, and do not put the circles back. */}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem", marginBottom: "0.75rem" }}>
        <button className="stc-act primary" aria-label="Add item" title="Add item" onClick={() => startEdit()}>
          <span style={{ fontSize: "1.15rem", lineHeight: 1 }}>+</span>
        </button>
        <button className="stc-act" aria-label="File a box from a photo" title="File a box from a photo — one AI call drafts the item list (pay-per-use from your Oriora wallet), you review before saving" onClick={() => setShowFileBox(true)}>
          <Camera size={16} />
        </button>
        <TagMenu
          backup={
            <BackupMenu
              appName="Stash" fileSlug="stash" appScheme="oriora-stash"
              hasData={items.length > 0} onRestored={reload}
              onExportCsv={items.length > 0 ? doExport : undefined}
            />
          }
          account={<AccountMenu email={email} appSlug="oriora-stash" appName="Oriora Stash" onSignedOut={onSignedOut} />}
        />
      </div>

      <NextOutBoard items={items} onPick={(season) => setFilter({ kind: "season", value: season })} />

      {/* 🔴 THE ASSISTANT ENTRY — a stencilled crate label on the next-out rail, replacing the
          floating "Chat" pill. onAsk opens the same sheet: moved, never removed. */}
      <AskCrate onAsk={() => setChatOpen(true)} />

      {/* R4 — the stat hero is one worded line now. */}
      {items.length > 0 && (
        <p className="stat-line">
          {titleCase(spellNum(summary.totalItems))} item{summary.totalItems === 1 ? "" : "s"} across {spellNum(places.length)} place{places.length === 1 ? "" : "s"}.
          {summary.neverUsed > 0 ? ` ${titleCase(spellNum(summary.neverUsed))} not yet logged as used.` : ""}
        </p>
      )}

      {/* R2 — the lookup answers the tagline: the Ask door sits near the top. */}
      <AskStash items={items} />

      {/* R1 — the MAP OF PLACES leads. */}
      {items.length === 0 ? (
        /* READY-STATE 2026-09-19 — this was one card with a crate illustration and a sentence, so
           the three lenses that make this a stash and not a list (what is due out this season,
           what has never come out, where things live) were invisible until items existed. The
           lanes are now drawn empty, and one line files the first thing into them. */
        <StartShelf onFiled={reload} />
      ) : filter ? (
        <>
          <div className="sec-head">
            <h2><SeasonMark season={filter.value} size={18} /> {titleCase(filter.value)} <span className="muted tnum" style={{ fontWeight: 400 }}>({filtered.length})</span></h2>
            <button className="btn btn-sm btn-pill" onClick={() => setFilter(null)}>Back to places</button>
          </div>
          {filtered.length === 0 ? (
            <div className="card" style={{ padding: "1.5rem", textAlign: "center" }}>
              <div className="muted" style={{ fontSize: "var(--fs-sm)" }}>Nothing here for this season.</div>
            </div>
          ) : (
            <PlaceGrouped items={filtered} onEdit={startEdit} onDelete={setConfirmDelete} onMarkUsed={doMarkUsed} />
          )}
        </>
      ) : openPlace ? (
        <>
          <div className="sec-head">
            <h2><PlaceMark place={openPlace} size={22} /> {openPlace} <span className="muted tnum" style={{ fontWeight: 400 }}>({placeItems.length})</span></h2>
            <button className="btn btn-sm btn-pill" onClick={() => setOpenPlace(null)}>All places</button>
          </div>
          {placeItems.length === 0 ? (
            <div className="card" style={{ padding: "1.75rem", textAlign: "center" }}>
              <img src={emptyPlaceUrl} alt="" style={{ width: 120, height: 90 }} />
              <div className="muted" style={{ fontSize: "var(--fs-sm)", marginTop: 6 }}>Nothing stored here yet.</div>
            </div>
          ) : (
            <PlaceGrouped items={placeItems} onEdit={startEdit} onDelete={setConfirmDelete} onMarkUsed={doMarkUsed} />
          )}
        </>
      ) : (
        <>
          <div className="sec-head">
            <h2>Your places</h2>
            <span className="muted" style={{ fontSize: "var(--fs-xs)" }}>{places.length} area{places.length === 1 ? "" : "s"} · {facets.locations.length} spot{facets.locations.length === 1 ? "" : "s"}</span>
          </div>
          <div className="place-map">
            {places.map(([place, its]) => (
              <button key={place} type="button" className={`place-card${place === "Elsewhere" ? " elsewhere" : ""}`} onClick={() => setOpenPlace(place)}>
                {/* the crate face, then its stencilled label plate — see .place-card in styles.css */}
                <span className="place-mark"><PlaceMark place={place} size={30} /></span>
                <span className="place-plate">
                  <span className="place-card-name" style={{ display: "block" }}>{place}</span>
                  <span className="place-card-meta" style={{ display: "block" }}>{its.length} item{its.length === 1 ? "" : "s"}{its.some((i) => !i.last_used_on) ? " · some unused" : ""}</span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {/* The headline AI feature: What do I have for [season]? */}
      <div ref={recapRef}>
        <SeasonRecap items={items} seasons={facets.seasons} />
      </div>

      {showAdd && (
        <ItemForm editing={editing} facets={facets} onClose={() => { setShowAdd(false); setEditing(null); }} onSaved={() => { setShowAdd(false); setEditing(null); void reload(); }} />
      )}

      {showFileBox && (
        <FileBoxSheet facets={facets} onClose={() => setShowFileBox(false)} onSaved={() => { setShowFileBox(false); void reload(); }} />
      )}

      {confirmDelete && (
        <ModalShell title="Delete item?" onCancel={() => setConfirmDelete(null)}>
          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 1rem" }}>
            Remove "{confirmDelete.name}" from your catalog? This can't be undone.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: "0.4rem" }}>
            <button className="btn" style={{ fontSize: "var(--fs-xs)" }} onClick={() => setConfirmDelete(null)}>Cancel</button>
            <button className="btn btn-primary" style={{ fontSize: "var(--fs-xs)", background: "var(--rose)", borderColor: "var(--rose)" }} onClick={() => doDelete(confirmDelete.id)}>Delete</button>
          </div>
        </ModalShell>
      )}

      {/* Chat panel — a natural-language way to use the app by typing. Purely additive: the launcher
          opens a slide-over that drives the SAME store capabilities. onChanged → reload(). */}
      {/* 🔴 IDENTITY (2026-08-30): the floating "Chat" pill that used to launch this was DELETED —
          same pill, same corner, same label in ~15 sibling apps. The assistant is NOT removed and
          must never be: its entry is the .stc-crate label on the next-out rail, which calls this
          same setChatOpen. If a future change moves it, REPLACE it — never drop it. */}
      <aside className={`stc-sheet ${chatOpen ? "open" : ""}`} aria-hidden={!chatOpen}>
        <div className="stc-head">
          <span className="row" style={{ gap: "0.35rem" }}><Sparkles size={14} /> Assistant — type to add, find, or recap</span>
          <button className="stc-close" onClick={() => setChatOpen(false)}>Close ↓</button>
        </div>
        <Chat onChanged={reload} />
      </aside>
    </main>
  );
}

/**
 * The month each curated season is next wanted OUT of storage (1 = January).
 *
 * ⚠️ MONTHS, NOT DATES — deliberately. Easter moves between 22 March and 25 April and US
 * Thanksgiving is the fourth Thursday, so a day-precise countdown would be a fabricated number in
 * front of the user. A month is a fact we can stand behind. Anything NOT in this table (the user's
 * own tokens like "loft stuff") gets NO position at all rather than a guessed one — see
 * `undated` in NextOutBoard.
 *
 * Northern-hemisphere, matching the meteorological seasons the rest of the fleet uses. A user in
 * the southern hemisphere would want these six months shifted; that is a real limitation, not a
 * bug, and it is why the board never claims more precision than "October".
 */
const SEASON_MONTH: Record<string, number> = {
  winter: 12, ski: 12, christmas: 12,
  spring: 3, easter: 4,
  summer: 6, camping: 6, beach: 6,
  autumn: 9, fall: 9, halloween: 10, rainy: 10,
  thanksgiving: 11,
};
const MONTH_NAME = ["", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

/** Months from `from` (1-12) forward to `target`, 0 when it is this month. */
const monthsUntil = (from: number, target: number) => (target - from + 12) % 12;

/**
 * "Next out" — ledger row 2b.
 *
 * The old home led with a count of items and then a flat list sorted `season ASC, name ASC`, so a
 * seasonal store answered "what do I own?" alphabetically. The question someone actually opens this
 * app with is "X is coming — what have I got, and which box is it in?" Two things follow:
 *
 *  • RANK BY WHAT IS NEEDED SOONEST, not alphabetically. Halloween in September beats Beach.
 *  • GROUP BY PLACE. These are physical objects in bins; the ledger's primitive table calls for
 *    place-grouping, and a bin name is the one piece of information that makes the catalogue
 *    actually save you a trip to the attic.
 *
 * The never-used count per season is stated as a plain fact — the app's fence forbids telling
 * anyone to declutter, and "6 not yet logged as used" is an observation, not a nudge.
 */
function NextOutBoard({ items, onPick }: { items: Item[]; onPick: (season: string) => void }) {
  const board = useMemo(() => {
    const month = new Date().getMonth() + 1;
    const bySeason = new Map<string, Item[]>();
    for (const i of items) {
      const s = (i.season || "").trim().toLowerCase();
      if (!s) continue;
      bySeason.set(s, [...(bySeason.get(s) ?? []), i]);
    }
    const rows = [...bySeason.entries()].map(([season, its]) => ({
      season,
      items: its,
      units: its.reduce((n, i) => n + (i.quantity || 1), 0),
      neverUsed: its.filter((i) => !i.last_used_on).length,
      month: SEASON_MONTH[season] ?? null,
    }));
    const dated = rows
      .filter((r) => r.month !== null)
      .sort((a, b) => monthsUntil(month, a.month as number) - monthsUntil(month, b.month as number)
        || b.items.length - a.items.length);
    const undated = rows.filter((r) => r.month === null).sort((a, b) => b.items.length - a.items.length);
    return { dated, undated, month };
  }, [items]);

  if (!board.dated.length && !board.undated.length) return null;
  const lead = board.dated[0] ?? null;

  // Where the leading season's things actually live, biggest bin first.
  const bins = lead
    ? [...lead.items.reduce((m, i) => {
        const k = (i.location || "").trim() || "No location set";
        return m.set(k, [...(m.get(k) ?? []), i]);
      }, new Map<string, Item[]>()).entries()]
        .map(([location, its]) => ({ location, count: its.length, names: its.slice(0, 4).map((i) => i.name) }))
        .sort((a, b) => b.count - a.count)
    : [];

  return (
    <div className="card fade-up" style={{ padding: "1rem 1.15rem", marginBottom: "1rem" }}>
      {lead && (
        <>
          <div className="row" style={{ justifyContent: "space-between", alignItems: "baseline", gap: "0.5rem" }}>
            <div className="eyebrow" style={{ marginBottom: 0 }}>
              {monthsUntil(board.month, lead.month as number) === 0 ? "Out now" : `Next out · ${MONTH_NAME[lead.month as number]}`}
            </div>
            <button className="link" style={{ fontSize: "var(--fs-xs)" }} onClick={() => onPick(lead.season)}>See all</button>
          </div>
          <div className="row" style={{ gap: "0.45rem", alignItems: "baseline", marginTop: "0.15rem" }}>
            <SeasonMark season={lead.season} size={20} />
            <span className="serif" style={{ fontSize: "1.35rem", fontWeight: 600 }}>{titleCase(lead.season)}</span>
            <span className="muted tnum" style={{ fontSize: "var(--fs-xs)" }}>
              {lead.items.length} {lead.items.length === 1 ? "item" : "items"}
              {lead.units !== lead.items.length ? ` · ${lead.units} units` : ""}
            </span>
          </div>

          {bins.map((b) => (
            <div key={b.location} className="row" style={{ justifyContent: "space-between", alignItems: "baseline", gap: "0.6rem", borderTop: "1px solid var(--line)", padding: "0.5rem 0" }}>
              <span style={{ minWidth: 0 }}>
                <span className="row" style={{ gap: "0.3rem", fontWeight: 600, fontSize: "var(--fs-sm)" }}>
                  <MapPin size={12} style={{ flexShrink: 0, opacity: 0.6 }} /> {b.location}
                </span>
                <span className="muted" style={{ fontSize: "var(--fs-xxs)", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {b.names.join(", ")}{b.count > b.names.length ? ` +${b.count - b.names.length} more` : ""}
                </span>
              </span>
              <span className="tnum muted" style={{ flexShrink: 0, fontSize: "var(--fs-xs)" }}>{b.count}</span>
            </div>
          ))}

          {lead.neverUsed > 0 && (
            <div className="muted" style={{ fontSize: "var(--fs-xxs)", marginTop: "0.5rem" }}>
              {lead.neverUsed} of these {lead.neverUsed === 1 ? "has" : "have"} no use logged yet
            </div>
          )}
        </>
      )}

      {board.dated.length > 1 && (
        <>
          <div className="eyebrow" style={{ marginTop: "0.9rem", marginBottom: "0.1rem" }}>After that</div>
          <div className="row" style={{ gap: "0.35rem", flexWrap: "wrap" }}>
            {board.dated.slice(1, 6).map((r) => (
              <button key={r.season} className="chip" onClick={() => onPick(r.season)}>
                <SeasonMark season={r.season} size={15} /> {titleCase(r.season)} · {MONTH_NAME[r.month as number].slice(0, 3)} · {r.items.length}
              </button>
            ))}
          </div>
        </>
      )}

      {board.undated.length > 0 && (
        <>
          {/* No month in the table — shown plainly rather than slotted into a made-up position. */}
          <div className="eyebrow" style={{ marginTop: "0.9rem", marginBottom: "0.1rem" }}>Any time</div>
          <div className="row" style={{ gap: "0.35rem", flexWrap: "wrap" }}>
            {board.undated.slice(0, 5).map((r) => (
              <button key={r.season} className="chip" onClick={() => onPick(r.season)}>
                <SeasonMark season={r.season} size={15} /> {titleCase(r.season)} · {r.items.length}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Container rows: items grouped by their full location within an opened place or season view.
function PlaceGrouped({ items, onEdit, onDelete, onMarkUsed }: {
  items: Item[]; onEdit: (i: Item) => void; onDelete: (i: Item) => void; onMarkUsed: (id: string) => void;
}) {
  const groups = useMemo(() => {
    const m = new Map<string, Item[]>();
    for (const i of items) { const k = (i.location || "").trim() || "Unplaced"; m.set(k, [...(m.get(k) ?? []), i]); }
    return [...m.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [items]);
  return (
    <div>
      {groups.map(([loc, its]) => (
        <div key={loc} className="container-row">
          <div className="container-label">{titleCase(loc)} <span className="muted tnum" style={{ textTransform: "none", fontWeight: 400 }}>· {its.length}</span></div>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: "0.45rem" }}>
            {its.map((i) => (
              <ItemTile key={i.id} item={i} onEdit={() => onEdit(i)} onDelete={() => onDelete(i)} onMarkUsed={() => onMarkUsed(i.id)} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// SummaryHero RETIRED (R4) — the stat hero is one worded .stat-line on Home.
function SummaryHero({ summary }: { summary: ReturnType<typeof summarize> }) {
  return (
    <div className="hero row" style={{ gap: "1rem" }}>
      <span style={{ fontSize: "2.4rem", lineHeight: 1 }}>{summary.totalItems === 0 ? <Package size={40} /> : <PlaceMark place="box" size={40} />}</span>
      <div>
        <div className="serif" style={{ fontSize: "1.9rem", fontWeight: 600, lineHeight: 1.1 }}>
          {summary.totalItems === 0 ? "Empty catalog" : `${summary.totalItems} ${summary.totalItems === 1 ? "item" : "items"}`}
        </div>
        <div style={{ fontSize: "var(--fs-sm)", opacity: 0.85 }}>{summaryCopy(summary)}</div>
        {summary.neverUsed > 0 && (
          <div style={{ fontSize: "var(--fs-xs)", opacity: 0.7, marginTop: 2 }}>{summary.neverUsed} not yet logged as used</div>
        )}
      </div>
    </div>
  );
}

function FilterBar({ facets, filter, setFilter }: { facets: Facets; filter: Filter; setFilter: (f: Filter) => void }) {
  const Group = ({ kind, label, values }: { kind: "season" | "location" | "category"; label: string; values: string[] }) => {
    if (values.length === 0) return null;
    return (
      <div style={{ marginBottom: "0.5rem" }}>
        <div className="muted" style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{label}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.35rem" }}>
          {values.map((v) => {
            const active = filter?.kind === kind && filter.value.toLowerCase() === v.toLowerCase();
            return (
              <button key={v} type="button" className={`chip${active ? " selected" : ""}`}
                onClick={() => setFilter(active ? null : { kind, value: v })}>
                {kind === "season" ? <><SeasonMark season={v} size={14} /> {titleCase(v)}</> : titleCase(v)}
              </button>
            );
          })}
        </div>
      </div>
    );
  };
  return (
    <div className="card" style={{ padding: "0.9rem 1.1rem", marginTop: "1.25rem" }}>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: "0.6rem" }}>
        <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)" }}>Filter</div>
        {filter && <button className="btn" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem" }} onClick={() => setFilter(null)}>Clear</button>}
      </div>
      <Group kind="season" label="Season" values={facets.seasons} />
      <Group kind="location" label="Location" values={facets.locations} />
      <Group kind="category" label="Category" values={facets.categories} />
    </div>
  );
}

// ── The headline AI feature: "What do I have for [season]?" ───────────────────
type RecapErr = { code: BrainErrorCode | "empty"; message: string };

function SeasonRecap({ items, seasons }: { items: Item[]; seasons: string[] }) {
  const [season, setSeason] = useState<string>("");
  const [text, setText] = useState<string | null>(null);
  const [usedSeason, setUsedSeason] = useState<string | null>(null);
  const [signals, setSignals] = useState<StashSignals | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);

  // Default to the first cataloged season once items load.
  useEffect(() => {
    if (!season && seasons.length > 0) setSeason(seasons[0]);
  }, [seasons, season]);

  async function run() {
    if (!season) return;
    setBusy(true); setErr(null); setText(null); setSignals(null);
    try {
      const sig = await store.computeSignals(todayLocal(), season);
      const r = await recapSeason(sig);
      const narration = r.narration.trim();
      if (!narration) {
        setErr({ code: "empty", message: "Nothing cataloged for that season yet — add a few items first." });
      } else {
        setText(narration);
        setSignals(sig);
        setUsedSeason(season);
      }
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Recap failed — please try again." });
    } finally { setBusy(false); }
  }

  if (items.length === 0) return null;
  return (
    <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: "3px solid var(--accent)", marginTop: "1rem" }}>
      <div className="row" style={{ fontWeight: 600, fontSize: "var(--fs-sm)", gap: "0.35rem", flexWrap: "wrap" }}>
        <Sparkles size={14} style={{ color: "var(--accent)", flexShrink: 0 }} /> What do I have for…
      </div>

      <div className="row" style={{ gap: "0.5rem", marginTop: "0.75rem", alignItems: "stretch" }}>
        <select value={season} onChange={(e) => setSeason(e.target.value)} style={{ flex: 1, fontSize: "var(--fs-sm)" }} aria-label="Season">
          {seasons.length === 0 && <option value="">No seasons yet</option>}
          {seasons.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
        </select>
        <button className="btn btn-primary row" style={{ fontSize: "var(--fs-sm)", padding: "0.45rem 0.85rem", gap: "0.35rem", flexShrink: 0 }} disabled={busy || !season} onClick={run}>
          {busy ? "Looking…" : text ? (<><RefreshCw size={12} /> Again</>) : "Recap"}
        </button>
      </div>

      {!busy && !text && !err && (
        <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 6 }}>
          Narrates everything you own for that season — how many items, across which bins, and where each lives.
        </div>
      )}

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: "0.4rem" }}>
            <Sparkles size={12} style={{ color: "var(--accent)" }} /> Counting what you have for {titleCase(season)}…
          </span>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "92%" }} />
          <div className="skeleton-line" style={{ width: "70%" }} />
        </div>
      )}

      {err && !busy && <ReflectionError err={err} onRetry={run} />}

      {text && !busy && (
        <div style={{ marginTop: "0.7rem" }} aria-live="polite">
          {usedSeason && <div className="season-pill" style={{ marginBottom: 8 }}><span className="season-mark"><SeasonMark season={usedSeason} size={16} /></span> {titleCase(usedSeason)}</div>}
          <p style={{ fontSize: "var(--fs-sm)", color: "var(--ink-2)", lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{text}</p>
          <div style={{ marginTop: 8 }}><OrioraBadge /></div>
          {signals?.focus && <FocusEvidence focus={signals.focus} />}
          <div className="row" style={{ marginTop: 10 }}>
            <span className="muted" style={{ fontSize: 10 }}>Describes what you own and where — not styling or shopping advice.</span>
          </div>
        </div>
      )}
    </div>
  );
}

// The receipts: the computed location breakdown the recap drew from — purely observational.
function FocusEvidence({ focus }: { focus: NonNullable<StashSignals["focus"]> }) {
  const chips: { label: string; title: string }[] = [];
  chips.push({ label: `${focus.total_items} item${focus.total_items === 1 ? "" : "s"}`, title: `${focus.total_units} total units cataloged for ${titleCase(focus.season)}` });
  for (const g of focus.locations.slice(0, 4)) {
    chips.push({ label: `${g.location} · ${g.items}`, title: `${g.items} item${g.items === 1 ? "" : "s"} (${g.units} units) in ${g.location}` });
  }
  if (focus.never_used_items.length > 0) {
    chips.push({ label: `never used · ${focus.never_used_items.length}`, title: `Not logged as used: ${focus.never_used_items.join(", ")}` });
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: "0.6rem" }}>
      {chips.map((c) => (
        <span key={c.label} className="chip tnum" title={c.title}
          style={{ padding: "0.1rem 0.45rem", fontSize: 10, background: "var(--accent-soft)", borderColor: "transparent", color: "var(--accent-soft-ink)" }}>
          {c.label}
        </span>
      ))}
    </div>
  );
}

// ── Ask your stash: NL query over the catalog (ceiling-safe; advice questions refused) ──
function AskStash({ items }: { items: Item[] }) {
  const [q, setQ] = useState("");
  const [answer, setAnswer] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<RecapErr | null>(null);

  async function ask() {
    const question = q.trim();
    if (!question) return;
    // Mirror the pre-LLM fence in the UI so the user sees the redirect instantly (no call billed).
    if (crossesFence(question)) { setAnswer(FENCE_REDIRECT); setRefused(true); setErr(null); return; }
    setBusy(true); setErr(null); setAnswer(null); setRefused(false);
    try {
      const sig = await store.computeSignals(todayLocal());
      const r = await askStash(question, sig);
      setAnswer(r.answer || "I couldn't find anything for that in your catalog.");
      setRefused(Boolean(r.refused));
    } catch (e) {
      if (e instanceof BrainError) setErr({ code: e.code, message: e.message });
      else setErr({ code: "unavailable", message: "Ask failed — please try again." });
    } finally { setBusy(false); }
  }

  if (items.length === 0) return null;
  return (
    <div className="door" style={{ marginTop: "0.25rem" }}>
      <div className="door-title">
        <Mark kind="ask" size={20} color="var(--accent)" /> Ask your stash
      </div>
      <form className="row" style={{ gap: "0.5rem", marginTop: "0.75rem" }} onSubmit={(e) => { e.preventDefault(); void ask(); }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Where are my winter boots? What's in the attic?" style={{ flex: 1, fontSize: "var(--fs-sm)" }} />
        <button type="submit" className="btn btn-primary" style={{ fontSize: "var(--fs-sm)", flexShrink: 0 }} disabled={busy || !q.trim()}>{busy ? "Asking…" : "Ask"}</button>
      </form>
      <div className="door-fence">
        Answers from your catalog — what you own and where. Not styling or shopping advice.
      </div>

      {busy && (
        <div aria-live="polite" style={{ marginTop: "0.75rem", display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 8 }}>
          <div className="skeleton-line" style={{ width: "100%" }} />
          <div className="skeleton-line" style={{ width: "80%" }} />
        </div>
      )}
      {err && !busy && <ReflectionError err={err} onRetry={() => void ask()} />}
      {answer && !busy && (
        <>
        <p aria-live="polite" style={{ fontSize: "var(--fs-sm)", color: refused ? "var(--ink-3)" : "var(--ink-2)", lineHeight: 1.6, marginTop: "0.7rem", marginBottom: 0, fontStyle: refused ? "italic" : "normal", whiteSpace: "pre-wrap" }}>
          {answer}
        </p>
        {!refused && <div style={{ marginTop: 8 }}><OrioraBadge /></div>}
        </>
      )}
    </div>
  );
}

function ReflectionError({ err, onRetry }: { err: RecapErr; onRetry: () => void }) {
  // Insufficient funds — point to the Oriora account on the web, no amounts shown.
  if (err.code === "insufficient_funds") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "rgba(217,147,58,0.1)", border: "1px solid rgba(217,147,58,0.3)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--warning)", flexShrink: 0, marginTop: 1 }} />
        <div>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 600 }}>Add funds to keep using AI</div>
          <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 2 }}>Your Oriora balance is too low. Add funds, then try again.</div>
        </div>
      </div>
    );
  }
  if (err.code === "managed_ai_blocked") {
    return (
      <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "flex-start", background: "var(--elev-2)", border: "1px solid var(--border)", borderRadius: 10, padding: "0.6rem 0.75rem" }}>
        <Sparkles size={15} style={{ color: "var(--accent)", flexShrink: 0, marginTop: 1 }} />
        <div className="muted" style={{ fontSize: "var(--fs-xs)" }}>{err.message}</div>
      </div>
    );
  }
  if (err.code === "empty") {
    return <div className="muted" style={{ fontSize: "var(--fs-xs)", marginTop: 10 }}>{err.message}</div>;
  }
  const retryable = err.code === "unavailable";
  return (
    <div className="row" style={{ marginTop: 10, gap: "0.6rem", alignItems: "center", justifyContent: "space-between" }}>
      <span style={{ fontSize: "var(--fs-xs)", color: "var(--rose)" }}>{err.message}</span>
      {retryable && (
        <button className="btn row" style={{ fontSize: "var(--fs-xs)", padding: "0.25rem 0.55rem", gap: "0.3rem", flexShrink: 0 }} onClick={onRetry}>
          <RefreshCw size={12} /> Retry
        </button>
      )}
    </div>
  );
}

function ItemTile({ item, onEdit, onDelete, onMarkUsed }: { item: Item; onEdit: () => void; onDelete: () => void; onMarkUsed: () => void }) {
  const [viewing, setViewing] = useState(false);
  const photoSrc = item.photo_ref.startsWith("http") || item.photo_ref.startsWith("data:") ? item.photo_ref : `file://${item.photo_ref}`;
  return (
    <div className="tile" style={{ cursor: "pointer" }} onClick={onEdit}>
      {item.photo_ref ? (
        <img
          src={photoSrc}
          alt=""
          title={titleCase(item.season)}
          onClick={(e) => { e.stopPropagation(); setViewing(true); }}
          style={{ width: 42, height: 42, borderRadius: 11, objectFit: "cover", flexShrink: 0, border: "1px solid var(--border-subtle)", cursor: "zoom-in" }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
        />
      ) : (
        <span className="tile-icon">{item.season ? <SeasonMark season={item.season} size={24} /> : <PlaceMark place={item.location || "box"} size={24} />}</span>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ gap: "0.4rem" }}>
          <div style={{ fontWeight: 600, fontSize: "var(--fs-sm)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
          {item.quantity > 1 && <span className="chip tnum" style={{ padding: "0 0.35rem", fontSize: 10 }}>×{item.quantity}</span>}
        </div>
        <div className="row" style={{ gap: "0.5rem", marginTop: 3 }}>
          {item.location && <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: 3 }}><MapPin size={11} /> {item.location}</span>}
          <span className="muted row" style={{ fontSize: "var(--fs-xs)", gap: 3, color: item.last_used_on ? "var(--ink-3)" : "var(--warning)" }}>
            <CalendarClock size={11} /> {lastUsedCopy(item.last_used_on)}
          </span>
        </div>
        {(item.season || item.occasion || item.category) && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem", marginTop: 5 }}>
            {item.season && <span className="chip" style={{ padding: "0.1rem 0.45rem", fontSize: 10 }}>{titleCase(item.season)}</span>}
            {item.category && <span className="chip" style={{ padding: "0.1rem 0.45rem", fontSize: 10 }}>{titleCase(item.category)}</span>}
            {item.occasion && <span className="chip" style={{ padding: "0.1rem 0.45rem", fontSize: 10 }}>{item.occasion}</span>}
          </div>
        )}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, flexShrink: 0 }}>
        <button className="btn" title="Mark used today" aria-label="Mark used today"
          style={{ padding: "0.3rem 0.4rem", border: "1px solid transparent", background: "none", color: "var(--success)" }}
          onClick={(ev) => { ev.stopPropagation(); onMarkUsed(); }}>
          <CheckCircle2 size={15} />
        </button>
        <button className="btn" title="Delete item" aria-label="Delete item"
          style={{ padding: "0.3rem 0.4rem", border: "1px solid transparent", background: "none", color: "var(--ink-3)" }}
          onClick={(ev) => { ev.stopPropagation(); onDelete(); }}>
          <Trash2 size={15} />
        </button>
      </div>
      {viewing && item.photo_ref && <PhotoViewer photos={[{ src: photoSrc, alt: item.name }]} onClose={() => setViewing(false)} />}
    </div>
  );
}

function ItemForm({ editing, facets, onClose, onSaved }: { editing: Item | null; facets: Facets; onClose: () => void; onSaved: () => void }) {
  const isEditing = editing !== null;
  const [name, setName] = useState(editing?.name ?? "");
  const [season, setSeason] = useState(editing?.season ?? "");
  const [category, setCategory] = useState(editing?.category ?? "");
  const [location, setLocation] = useState(editing?.location ?? "");
  const [occasion, setOccasion] = useState(editing?.occasion ?? "");
  const [quantity, setQuantity] = useState<number>(editing?.quantity ?? 1);
  const [notes, setNotes] = useState(editing?.notes ?? "");
  const [photoRef, setPhotoRef] = useState(editing?.photo_ref ?? "");
  const [neverUsed, setNeverUsed] = useState<boolean>(editing ? editing.last_used_on === null : true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // iPhone photo picking. The desktop file dialog is a SILENT NO-OP on iOS, so on iOS we trigger a
  // hidden <input type=file accept=image/*> (opens the native photo/camera sheet), then downscale the
  // picked image to a modest JPEG data URL kept on-device in photo_ref (never uploaded). Mirrors the
  // proven Curio pattern. Downscaling avoids storing a multi-MB original in the local SQLite row.
  const photoInputRef = useRef<HTMLInputElement>(null);

  async function pickPhoto() {
    setErr(null);
    // iOS AND web both go through the hidden <input type=file>: on web the Tauri dialog below is
    // the shim's null-returning stub, so "Add photo" was a SILENTLY DEAD button in the browser —
    // pressed live at 375 and 430, zero file-input clicks, zero DOM change (`probe-leads.mjs`
    // LEAD 3, 2026-08-28). The browser's own picker is the right surface there, and the downscale
    // handler below is platform-agnostic. macOS/Linux keep the native dialog (real file paths).
    if (platform() === "ios" || platform() === "web") { photoInputRef.current?.click(); return; } // sync → stays in the tap gesture
    try {
      const p = await openFileDialog({ multiple: false, filters: [{ name: "Image", extensions: ["png", "jpg", "jpeg", "gif", "webp", "heic"] }] });
      if (typeof p === "string") setPhotoRef(p);
    } catch { /* user cancelled */ }
  }

  function onPhotoInputChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ""; // reset so re-picking the same image refires
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result;
      if (typeof src !== "string") { setErr("Could not read that photo — try another."); return; }
      const img = new Image();
      img.onload = () => {
        const MAX = 1024;
        const scale = Math.min(1, MAX / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { setPhotoRef(src); return; }
        ctx.drawImage(img, 0, 0, w, h);
        try { setPhotoRef(canvas.toDataURL("image/jpeg", 0.82)); }
        catch { setPhotoRef(src); }
      };
      img.onerror = () => setPhotoRef(src); // unsupported decode (e.g. some HEIC) → keep original
      img.src = src;
    };
    reader.onerror = () => setErr("Could not read that photo — try another.");
    reader.readAsDataURL(file);
  }

  // Merge curated suggestions with what's already in the catalog (deduped).
  const seasonOpts = useMemo(() => dedupeTokens([...DEFAULT_SEASONS, ...facets.seasons]), [facets.seasons]);
  const categoryOpts = useMemo(() => dedupeTokens([...DEFAULT_CATEGORIES, ...facets.categories]), [facets.categories]);
  const locationOpts = useMemo(() => dedupeFree([...DEFAULT_LOCATIONS, ...facets.locations]), [facets.locations]);

  async function save() {
    if (!name.trim()) { setErr("Give the item a name."); return; }
    setSaving(true); setErr(null);
    try {
      const base = {
        name: name.trim(),
        season: normalizeToken(season),
        category: normalizeToken(category),
        location: location.trim(),
        occasion: occasion.trim(),
        quantity: Math.max(1, quantity || 1),
        notes: notes.trim(),
        photo_ref: photoRef.trim(),
      };
      // "Haven't used this" is a REAL two-way control, not a label. Ticked → clear the use-log;
      // unticked on a row that has no date → stamp it used today (the same thing the tile's
      // "Mark used today" does). Before 2026-08-28 the create path ignored the checkbox outright
      // (`last_used_on: null` unconditionally) and the edit path only handled the ticked
      // direction, so unticking it did nothing at all: saved, reopened, still "Never used"
      // (reproduced live, `probe-leads.mjs` LEAD 2).
      if (isEditing) {
        const patch: Parameters<typeof store.updateItem>[1] = { ...base };
        patch.last_used_on = neverUsed ? null : (editing!.last_used_on ?? todayLocal());
        await store.updateItem(editing!.id, patch);
      } else {
        await store.createItem({ ...base, last_used_on: neverUsed ? null : todayLocal() });
      }
      onSaved();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg === "name_required" ? "Give the item a name." : `Could not save — try again. (${msg})`);
      setSaving(false);
    }
  }

  return (
    <ModalShell title={isEditing ? "Edit item" : "Add item"} onCancel={onClose} wide>
      <Field label="Item name">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. warm-white string lights" autoFocus />
      </Field>

      <Field label="Photo (optional)">
        <div className="row" style={{ gap: "0.6rem", alignItems: "center" }}>
          {photoRef ? (
            <img
              src={photoRef.startsWith("http") || photoRef.startsWith("data:") ? photoRef : `file://${photoRef}`}
              alt=""
              style={{ width: 56, height: 56, borderRadius: 10, objectFit: "cover", flexShrink: 0, border: "1px solid var(--border-subtle)" }}
              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
            />
          ) : (
            <span style={{ width: 56, height: 56, borderRadius: 10, flexShrink: 0, background: "var(--surface-2)", border: "1px solid var(--border-subtle)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--ink-3)" }}>
              <ImageIcon size={22} />
            </span>
          )}
          <button type="button" className="btn row" style={{ gap: "0.35rem", fontSize: "var(--fs-xs)" }} onClick={pickPhoto}>
            <Camera size={14} /> {photoRef ? "Replace" : "Add photo"}
          </button>
          {photoRef && (
            <button type="button" className="btn" style={{ fontSize: "var(--fs-xs)", color: "var(--ink-3)" }} onClick={() => setPhotoRef("")}>Remove</button>
          )}
        </div>
        {/* iOS native photo/camera sheet — hidden input the pickPhoto() gesture clicks. */}
        <input ref={photoInputRef} type="file" accept="image/*" style={{ display: "none" }} onChange={onPhotoInputChange} />
      </Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <Field label="Season / occasion" grow>
          <input list="season-opts" value={season} onChange={(e) => setSeason(e.target.value)} placeholder="christmas" />
          <datalist id="season-opts">{seasonOpts.map((s) => <option key={s} value={titleCase(s)} />)}</datalist>
        </Field>
        <Field label="Category" grow>
          <input list="cat-opts" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="lights" />
          <datalist id="cat-opts">{categoryOpts.map((c) => <option key={c} value={titleCase(c)} />)}</datalist>
        </Field>
      </div>

      <Field label="Stored where?">
        <input list="loc-opts" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Attic Bin 2" />
        <datalist id="loc-opts">{locationOpts.map((l) => <option key={l} value={l} />)}</datalist>
      </Field>

      <div className="row" style={{ gap: "0.6rem", alignItems: "flex-start" }}>
        <Field label="Occasion tag (optional)" grow>
          <input value={occasion} onChange={(e) => setOccasion(e.target.value)} placeholder="e.g. Christmas tree" />
        </Field>
        <div style={{ width: 110 }}>
          <Field label="Quantity">
            <input type="number" min={1} value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)} />
          </Field>
        </div>
      </div>

      <Field label="Notes (optional)">
        <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything worth remembering" />
      </Field>

      <label className="row" style={{ gap: "0.45rem", fontSize: "var(--fs-xs)", color: "var(--ink-2)", marginBottom: "1rem", cursor: "pointer" }}>
        <input type="checkbox" checked={neverUsed} onChange={(e) => setNeverUsed(e.target.checked)} style={{ width: "auto" }} />
        Haven't used this {isEditing ? "(clears the use-log)" : "yet"}
      </label>

      {err && <div style={{ fontSize: "var(--fs-xs)", color: "var(--rose)", marginBottom: "0.75rem" }}>{err}</div>}
      <div className="row" style={{ justifyContent: "flex-end", gap: "0.5rem" }}>
        <button className="btn" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={!name.trim() || saving}>{saving ? "Saving…" : isEditing ? "Update" : "Add item"}</button>
      </div>
    </ModalShell>
  );
}

function Field({ label, children, grow }: { label: string; children: React.ReactNode; grow?: boolean }) {
  return (
    <div style={{ marginBottom: "0.85rem", flex: grow ? 1 : undefined, minWidth: grow ? 0 : undefined }}>
      <div style={{ fontWeight: 600, fontSize: "var(--fs-xs)", marginBottom: "0.35rem" }}>{label}</div>
      {children}
    </div>
  );
}

function dedupeTokens(raw: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of raw) {
    const n = normalizeToken(r);
    if (!n || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out.sort();
}
function dedupeFree(raw: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const r of raw) {
    const t = r.trim();
    const k = t.toLowerCase();
    if (!t || seen.has(k)) continue;
    seen.add(k);
    out.push(t);
  }
  return out.sort((a, b) => a.localeCompare(b));
}

// ── FILE A BOX FROM A PHOTO (§5b 2026-08-19) — the anti-filing-friction flow. One photo → a DRAFT
// list the customer reviews (untick, rename, set the location) → bulk-add. Nothing saves until
// they confirm; the photo never persists anywhere.
function FileBoxSheet({ facets, onClose, onSaved }: {
  facets: { locations: string[]; categories: string[] };
  onClose: () => void; onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [drafts, setDrafts] = useState<(FiledBoxItem & { keep: boolean })[]>([]);
  const [location, setLocation] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  async function onPick(ev: ChangeEvent<HTMLInputElement>) {
    const file = ev.target.files?.[0];
    ev.target.value = "";
    if (!file) return;
    setBusy(true); setErr(null); setNote("");
    try {
      const dataUrl = await readDownscaledPhoto(file);
      const r = await fileBoxFromPhoto(dataUrl);
      if (r.items.length === 0) { setErr(r.note || "Couldn't make out any items — try a clearer photo with the box open."); return; }
      setDrafts(r.items.map((i) => ({ ...i, keep: true })));
      setNote(r.note);
    } catch (e) {
      setErr(e instanceof BrainError ? e.message : "That failed — please try again.");
    } finally { setBusy(false); }
  }

  async function saveAll() {
    const chosen = drafts.filter((d) => d.keep && d.name.trim());
    if (chosen.length === 0) { setErr("Nothing ticked to add."); return; }
    setSaving(true); setErr(null);
    try {
      for (const d of chosen) {
        await store.createItem({ name: d.name.trim(), category: d.category || undefined, season: d.season || undefined, location: location.trim() || undefined });
      }
      onSaved();
    } catch { setErr("Couldn't save — some items may have been added."); setSaving(false); }
  }

  return (
    <ModalShell title="File a box from a photo" onCancel={onClose} wide>
      {drafts.length === 0 ? (
        <>
          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0 0 0.8rem" }}>
            Photograph the open box, shelf or pile. One AI call drafts the item list — you review every
            line before anything is saved. The photo is read for this call and never kept.
          </p>
          <button className="btn btn-primary row" style={{ gap: "0.4rem" }} disabled={busy} onClick={() => inputRef.current?.click()}>
            {busy ? "Reading the photo…" : (<><Camera size={15} /> Choose or take a photo</>)}
          </button>
          {/* Billing cue at the door: this button fires the billed vision call, so the cost is
              named here rather than left to the account sheet. Same shape as the fleet's other
              cues (KnowCode's "charged per ask on your own key — no subscription"). */}
          <p className="muted" style={{ fontSize: "var(--fs-xs)", margin: "0.5rem 0 0" }}>
            Pay-per-use from your Oriora wallet — no subscription.
          </p>
          <input ref={inputRef} type="file" accept="image/*" onChange={(e) => void onPick(e)} style={{ display: "none" }} />
        </>
      ) : (
        <>
          {note && <p className="muted" style={{ fontSize: "0.8rem", margin: "0 0 0.5rem" }}>{note}</p>}
          <div style={{ maxHeight: "44vh", overflowY: "auto", display: "grid", gap: 6, marginBottom: "0.7rem" }}>
            {drafts.map((d, i) => (
              <div key={i} className="row" style={{ gap: 6, alignItems: "center", flexWrap: "nowrap" }}>
                <input type="checkbox" checked={d.keep} onChange={(e) => setDrafts((cur) => cur.map((x, xi) => xi === i ? { ...x, keep: e.target.checked } : x))} />
                <input value={d.name} onChange={(e) => setDrafts((cur) => cur.map((x, xi) => xi === i ? { ...x, name: e.target.value } : x))} style={{ flex: 1, minWidth: 0 }} />
                {(d.category || d.season) && <span className="muted" style={{ fontSize: "0.72rem", flexShrink: 0 }}>{[d.category, d.season].filter(Boolean).join(" · ")}</span>}
              </div>
            ))}
          </div>
          <label style={{ fontSize: "var(--fs-xs)", fontWeight: 600, display: "block", marginBottom: 4 }}>Where is this box stored?</label>
          <input list="filebox-locations" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="e.g. attic — red bin" style={{ marginBottom: "0.8rem", width: "100%" }} />
          <datalist id="filebox-locations">{facets.locations.map((l) => <option key={l} value={l} />)}</datalist>
          <div className="row" style={{ gap: "0.5rem", justifyContent: "flex-end" }}>
            <button className="btn-ghost" onClick={() => { setDrafts([]); setNote(""); }}>Retake</button>
            <button className="btn btn-primary" disabled={saving} onClick={() => void saveAll()}>
              {saving ? "Adding…" : `Add ${drafts.filter((d) => d.keep && d.name.trim()).length} item${drafts.filter((d) => d.keep && d.name.trim()).length === 1 ? "" : "s"}`}
            </button>
          </div>
        </>
      )}
      {err && <p style={{ color: "var(--danger, #c0392b)", fontSize: "var(--fs-xs)", marginTop: "0.6rem" }}>{err}</p>}
    </ModalShell>
  );
}

function ModalShell({ title, children, onCancel, wide }: { title: string; children: React.ReactNode; onCancel: () => void; wide?: boolean }) {
  return (
    <div role="dialog" aria-modal="true" aria-label={title}
      onClick={onCancel}
      style={{ position: "fixed", inset: 0, background: "rgba(42,33,24,0.45)", display: "grid", placeItems: "center", zIndex: 50, padding: "1rem" }}>
      {/* fill-then-cap: `width:100%` + `maxWidth:N`. The reverse (`width:N, maxWidth:100%`) resolves
          the 100% against this item's own grid track, so the card kept its full width and spilled
          off a 402px phone (the Round Card / Cellar bug, measured in the sim). */}
      <div className="card" onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: wide ? 460 : 340, padding: "1.5rem", boxShadow: "var(--shadow-2)", borderRadius: "var(--radius-dialog)", maxHeight: "90vh", overflowY: "auto" }}>
        <div className="serif" style={{ fontWeight: 600, fontSize: "1.1rem", marginBottom: "1rem" }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
